/**
 * Resource Section Card
 *
 * One section of the Resources page: its title, notes and items. Crew see it
 * as is; admins get a drag handle and actions through the two slots.
 */

import { useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { downloadResourceFile, type ResourceFile, type ResourceSection } from '../../api/resources';
import { triggerDownload } from '../../api/documents';
import { getErrorMessage } from '../../api/client';
import { formatFileSize } from '../../utils/fileFormatting';

interface ResourceSectionCardProps {
  section: ResourceSection;
  dragHandle?: ReactNode;
  actions?: ReactNode;
}

const rowClasses =
  'flex items-center justify-between gap-3 w-full min-h-[44px] px-4 py-2 text-left font-medium text-ink hover:bg-surface-hover focus-ring';

export default function ResourceSectionCard({ section, dragHandle, actions }: ResourceSectionCardProps) {
  return (
    <section className="bg-white border border-hairline">
      <div
        className={`flex items-start gap-2 px-4 py-3 ${
          section.items.length > 0 ? 'border-b border-hairline' : ''
        }`}
      >
        {dragHandle}
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-ink break-words">{section.title}</h2>
          {section.notes && (
            <p className="mt-1 text-sm text-ink-body whitespace-pre-wrap break-words">{section.notes}</p>
          )}
        </div>
        {actions}
      </div>

      {section.items.length > 0 && (
        <ul>
          {section.items.map((item) => (
            <li key={item.id} className="border-b border-hairline-light last:border-b-0">
              {item.file ? (
                <FileRow title={item.title} file={item.file} />
              ) : (
                <a href={item.url ?? undefined} target="_blank" rel="noopener noreferrer" className={rowClasses}>
                  <span className="min-w-0 break-words">{item.title}</span>
                  <ExternalLinkIcon className="w-4 h-4 shrink-0 text-ink-muted" />
                </a>
              )}
              {item.notes && (
                <p className="px-4 pb-3 text-sm text-ink-body whitespace-pre-wrap break-words">{item.notes}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A button, not a link: the download needs the Authorization header, which a
 * plain href can't carry.
 */
function FileRow({ title, file }: { title: string; file: ResourceFile }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      triggerDownload(await downloadResourceFile(file.id), file.fileName);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button type="button" onClick={handleDownload} disabled={downloading} className={rowClasses}>
      <span className="min-w-0 break-words">
        {title}
        <span className="block text-xs font-normal text-ink-muted">
          {downloading ? 'Downloading...' : `${fileTypeLabel(file.fileName)} · ${formatFileSize(file.fileSize)}`}
        </span>
      </span>
      <DownloadIcon className="w-4 h-4 shrink-0 text-ink-muted" />
    </button>
  );
}

function fileTypeLabel(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toUpperCase() : 'File';
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
    </svg>
  );
}
